import { ClickAwayListener } from '@mui/material';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'next-i18next';

import type { PoiCategoryId } from 'data/search/poi/poi-categories';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';
import { AmenitiesFilterContent } from './amenities-filter-content';
import styles from './amenities-filter-panel.module.scss';

export type AmenitiesFilterPanelType = 'drawer' | 'popover';

export interface AmenitiesFilterPanelProps {
  open: boolean;
  onClose: () => void;
  /** 'drawer' -> bottom sliding drawer appearing on mobile screens, 
   * 'popover' -> small floating bubble appearing beside bottom right map controls on desktop screens 
   **/
  panelType?: AmenitiesFilterPanelType;
  selectedIds?: ReadonlySet<PoiCategoryId>;
  setSelectedIds?: Dispatch<SetStateAction<ReadonlySet<PoiCategoryId>>>;
}

export function AmenitiesFilterPanel({
  open,
  onClose,
  panelType = 'drawer',
  selectedIds: selectedIdsProp,
  setSelectedIds: setSelectedIdsProp,
}: AmenitiesFilterPanelProps) {
  const { t } = useTranslation([Namespaces.SEARCH]);

  const [internalSelectedIds, setInternalSelectedIds] = useState<ReadonlySet<PoiCategoryId>>(() => new Set());
  const selectedIds = selectedIdsProp ?? internalSelectedIds;
  const setSelectedIds = setSelectedIdsProp ?? setInternalSelectedIds;

  const content = useMemo(
    () => (
      <AmenitiesFilterContent
        onClose={onClose}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
      />
    ),
    [onClose, selectedIds, setSelectedIds]
  );

  const dialogLabel = t(LOCALE_SEARCH_PATHS.POI_AMENITIES_FILTER_PANEL_TITLE);

  if (!open) {
    return null;
  }

  if (panelType === 'popover') {
    return (
      <ClickAwayListener onClickAway={onClose}>
        <div
          className={styles.popoverContainer}
          role="dialog"
          aria-modal="true"
          aria-label={dialogLabel}
          data-testid="amenities-filter-popover"
        >
          {content}
        </div>
      </ClickAwayListener>
    );
  }

  const drawerOverlay = (
    <div
      className={buildClassName(styles.container, styles.containerPortaled)}
      role="dialog"
      aria-modal="true"
      aria-label={dialogLabel}
      data-testid="amenities-filter-drawer"
    >
      <button
        type="button"
        className={styles.backdrop}
        onClick={onClose}
        aria-label={t('poi.amenities_filter_panel_dismiss', { defaultValue: 'Dismiss amenities filter' })}
      />
      <div className={styles.drawer}>
        {content}
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(drawerOverlay, document.body);
  }

  return drawerOverlay;
}
